// BarcodeDisplay: Renders a barcode image using JsBarcode + SVG, with print support
import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import { Box, Typography, Button, Tooltip, IconButton } from "@mui/material";
import { Print, ContentCopy } from "@mui/icons-material";

/**
 * Props:
 *   value     – the barcode string to render
 *   label     – optional human-readable label below the barcode (e.g. product title)
 *   sku       – optional SKU to display
 *   height    – barcode height in px (default 60)
 *   showPrint – show Print button (default true)
 */
const BarcodeDisplay = ({ value, label, sku, height = 60, showPrint = true }) => {
	const svgRef = useRef(null);

	useEffect(() => {
		if (!value || !svgRef.current) return;
		try {
			JsBarcode(svgRef.current, value, {
				format: "CODE128",
				height,
				displayValue: true,
				fontSize: 12,
				margin: 8,
			});
		} catch (err) {
			console.error("Barcode render error:", err);
		}
	}, [value, height]);

	const handlePrint = () => {
		if (!svgRef.current) return;
		const svgData = new XMLSerializer().serializeToString(svgRef.current);
		const printWin = window.open("", "_blank", "width=400,height=300");
		if (!printWin) return;
		printWin.document.write(`
			<html>
			<head><title>Barcode Label</title>
			<style>
				body { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 16px; font-family: sans-serif; }
				.label { font-size: 13px; margin-top: 4px; text-align: center; }
				.sku { font-size: 11px; color: #555; margin-top: 2px; }
			</style>
			</head>
			<body>
				${svgData}
				${label ? `<div class="label">${label}</div>` : ""}
				${sku ? `<div class="sku">SKU: ${sku}</div>` : ""}
				<script>window.onload = () => { window.print(); window.close(); }</script>
			</body>
			</html>
		`);
		printWin.document.close();
	};

	const handleCopy = () => {
		if (value) navigator.clipboard.writeText(value).catch(() => {});
	};

	if (!value) {
		return (
			<Typography variant="body2" color="text.secondary">
				No barcode assigned
			</Typography>
		);
	}

	return (
		<Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
			<svg ref={svgRef} />
			{label && <Typography variant="caption">{label}</Typography>}
			{sku && <Typography variant="caption" color="text.secondary">SKU: {sku}</Typography>}
			<Box display="flex" gap={1} mt={0.5}>
				{showPrint && (
					<Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint}>
						Print Label
					</Button>
				)}
				<Tooltip title="Copy barcode value">
					<IconButton size="small" onClick={handleCopy}>
						<ContentCopy fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>
		</Box>
	);
};

export default BarcodeDisplay;
